import { Controller } from "@hotwired/stimulus"
import flatpickr from "flatpickr"

export default class extends Controller {
  static targets = ["display", "form", "datepicker"]
  static values = {
    url: String,
    method: { type: String, default: "patch" }
  }

  connect() {
    this._flatpickrs = []
  }

  edit() {
    this.displayTarget.classList.add("hidden")
    this.formTarget.classList.remove("hidden")

    // Initialize flatpickr on date inputs
    if (this.hasDatepickerTarget) {
      this.datepickerTargets.forEach(input => {
        if (!input._flatpickr) {
          const fp = flatpickr(input, {
            dateFormat: "Y-m-d",
            altInput: true,
            altFormat: "d/m/y",
            allowInput: true
          })
          this._flatpickrs.push(fp)
          fp.open()
        } else {
          input._flatpickr.open()
        }
      })
    }

    const firstInput = this.formTarget.querySelector("input[type=text]:not(.flatpickr-input)")
    if (firstInput) {
      firstInput.focus()
      firstInput.select()
    }
  }

  cancel() {
    this._flatpickrs.forEach(fp => fp.close())
    this.formTarget.classList.add("hidden")
    this.displayTarget.classList.remove("hidden")
    this.formTarget.querySelectorAll("input[type=text]").forEach(input => {
      input.value = input.defaultValue
    })
  }

  keydown(event) {
    if (event.key === "Escape") {
      event.preventDefault()
      this.cancel()
    } else if (event.key === "Enter") {
      event.preventDefault()
      this.save()
    }
  }

  async save() {
    const form = this.formTarget
    const data = {}

    form.querySelectorAll("input, select").forEach(el => {
      if (el.name) data[el.name] = el.value
    })

    const csrfToken = document.querySelector("meta[name=csrf-token]")?.content

    try {
      const response = await fetch(this.urlValue, {
        method: this.methodValue.toUpperCase(),
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": csrfToken
        },
        body: JSON.stringify(data)
      })

      if (response.ok) {
        const result = await response.json()

        // Update display row cells
        this.displayTarget.innerHTML = result.display_html

        if (result.url) {
          this.urlValue = result.url
          this.methodValue = "patch"
        }

        // Update defaults
        form.querySelectorAll("input[type=text]").forEach(input => {
          input.defaultValue = input.value
        })

        this.formTarget.classList.add("hidden")
        this.displayTarget.classList.remove("hidden")

        // Update purchases total
        this.updateTotal()
      } else {
        const result = await response.json()
        console.error("Save failed:", result.errors)
      }
    } catch (error) {
      console.error("Save error:", error)
    }
  }

  updateTotal() {
    const table = this.element.closest("table")
    if (!table) return

    let total = 0
    table.querySelectorAll("tbody tr").forEach(row => {
      if (row.classList.contains("bg-gray-100")) return
      const amountCell = row.querySelector("td:nth-child(4)")
      if (!amountCell) return
      const currencySpan = amountCell.querySelector(".currency")
      if (currencySpan) {
        const text = currencySpan.textContent.replace(/[£,\s\u00A3]/g, "")
        const pence = Math.round(parseFloat(text) * 100)
        if (!isNaN(pence)) total += pence
      }
    })

    const totalRow = table.querySelector("tr.bg-gray-100 td:nth-child(2)")
    if (totalRow) {
      const pounds = total / 100
      const formatted = total % 100 === 0
        ? `£${pounds.toFixed(0)}`
        : `£${pounds.toFixed(2)}`
      totalRow.innerHTML = `<span class="currency ${total < 0 ? 'negative' : 'positive'}">${formatted}</span>`
    }
  }
}
